import Typography from '@mui/material/Typography'
import {
    useState,
} from 'react'

import Button from 'app/shared-components/Button'
import ButtonContainer from 'app/shared-components/ButtonContainer'
import {
    ServiceCenterFormType,
} from 'app/Apps/ContainerManagement/ServiceCenters/serviceCenters.types'
import ConfirmationDialog from 'app/shared-components/ConfirmationDialog'
import {
    ChangeApprovalRequestStatus,
} from 'app/enums/label.enums'
import changeRequestApprovalRequests from 'app/requests/Order/ChangeRequestApproval.requests'
import useOrderManagementRoutes from 'app/SkyNetRoutes/hooks/useOrderManagementRoutes'

import {
    ChangeRequestApprovalType,
    Order,
} from '../../orders.types'
import ContainerAvailabilityForecast from '../OrderConfirmation/ContainerAvailabilityForecast'
import useRequestWithConfirmation from './hooks/useRequestWithConfirmation'
import RequiredContainers from './RequiredContainers'
import useStyles from './changeRequestApproval.styles'
import GeneralInformation from './GeneralInformation'

const ChangeRequestApproval = ({
    order,
    requestApproval,
    serviceCenter,
}: Readonly<{
    order: Order,
    requestApproval: ChangeRequestApprovalType,
    serviceCenter: ServiceCenterFormType,
}>) => {
    const {
        classes,
    } = useStyles()

    const [
        value,
        setValue,
    ] = useState({
        ...order, ...requestApproval,
    })

    const {
        CustomerOrders: CustomerOrdersRoutes,
    } = useOrderManagementRoutes()

    const onSuccessUpdateStatus = () => {
        CustomerOrdersRoutes.Overview.navigate({
            params: {
                key: order?.orderNumber,
            },
        })
    }

    const confirmRequest = useRequestWithConfirmation({
        requestParamFn: changeRequestApprovalRequests.changeStatus,
        notificationId: 'confirmRequest',
        data: {
            version: 0,
            changeApprovalRequestStatus: ChangeApprovalRequestStatus.CONFIRMED,
            orderNumber: order?.orderNumber,
            requestedPickup: value.requestedPickup,
            estimatedPickup: value.estimatedPickup,
        },
        onSuccess: onSuccessUpdateStatus,
    })

    const rejectRequest = useRequestWithConfirmation({
        requestParamFn: changeRequestApprovalRequests.changeStatus,
        notificationId: 'rejectRequest',
        data: {
            version: 0,
            changeApprovalRequestStatus: ChangeApprovalRequestStatus.REJECTED,
            orderNumber: order?.orderNumber,
        },
        onSuccess: onSuccessUpdateStatus,
    })

    return (
        <div data-testid="changeRequestApproval">
            <RequiredContainers
                value={value}
                setValue={setValue}
            />
            <Typography className={classes.title}>Change Request Approval</Typography>
            <GeneralInformation
                requestApproval={requestApproval}
                serviceCenter={serviceCenter}
            />
            <ContainerAvailabilityForecast
                serviceCenterId={serviceCenter?.id}
                requiredContainers={order?.requiredContainers}
                orderNumber={order?.orderNumber}
                requestedHandoverDate={requestApproval?.pendingRequestedHandover}
            />
            <ButtonContainer>
                <Button
                    label="confirm request"
                    onClick={confirmRequest.askConfirmation}
                    saving={confirmRequest.isPending}
                    disabled={rejectRequest.isPending}
                />
                <Button
                    label="reject request"
                    onClick={rejectRequest.askConfirmation}
                    saving={rejectRequest.isPending}
                    secondary
                    disabled={confirmRequest.isPending}
                />
            </ButtonContainer>
            <ConfirmationDialog
                open={confirmRequest.openConfirmDialog}
                handleClose={confirmRequest.doAction}
                dialogTitle="CONFIRM REQUEST"
                dialogContent="Are you sure that you want to confirm the change? Once confirmed, the Requested Handover Date will be updated."
            />
            <ConfirmationDialog
                open={rejectRequest.openConfirmDialog}
                handleClose={rejectRequest.doAction}
                dialogTitle="REJECT REQUEST"
                isPositiveAlert
                positiveLabel="REJECT"
                negativeLabel="CANCEL"
                dialogContent="Are you sure that you want to reject the change? Once rejected, user will be notified."
            />
        </div>
    )
}

export default ChangeRequestApproval
